import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@repo/design-system-kit';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useSegment } from '@repo/poly-hooks/segment';
import { isMobile } from '../../../utils/device';
import { Error404 } from '../../svgs';
import styles from './error.module.css';

export type IdNowErrorKeys =
  | 'CONNECTION_ISSUES'
  | 'IDNOW_ISSUE'
  | 'NO_AUDIO'
  | 'NO_VIDEO'
  | 'WRONG_LANGUAGE'
  | 'BAD_AUDIO'
  | 'ID_NOT_SUPPORTED'
  | 'GENERIC'
  | 'BAD_VIDEO'
  | 'ID_DAMAGED'
  | 'AGENT_ABORT'
  | 'USER_ABORT'
  | 'NO_ID'
  | 'WRONG_PERSON'
  | 'IDENT_CODE_NOT_RECEIVED'
  | 'IDNOW_DISCONNECTED'
  | 'ID_EXPIRED'
  | 'ID_HOLOGRAM'
  | 'LIGHTNING'
  | 'VIDEO_GENERIC_ISSUE'
  | 'BACKGROUND_NOISE'
  | 'AUDIO_GENERIC_ISSUE'
  | 'WRONG_ID_CODE'
  | 'USER_ABORT_WHILE_WAITING'
  | 'OTHER_DEVICE'
  | 'ID_GENERIC_ISSUE'
  | 'STALLED_TIMEOUT'
  | 'CALL_QUALITY_FAIL'
  | 'NO_VIDEO_IDENT_CONSENT'
  | 'WRONG_ID_NUMBER'
  | 'QES_ISSUE'
  | 'WRONG_PHONE_NUMBER'
  | 'NO_AGENTS';

export interface IdNowErrorProps {
  onTryAgain: () => void;
  onSwitchToPhone: () => void;
  reason?: IdNowErrorKeys;
}

export function IdNowError({ onTryAgain, onSwitchToPhone, reason }: IdNowErrorProps): ReactNode {
  const intl = useIntl();
  const segment = useSegment();
  const faqHref = intl.formatMessage({ id: 'links.faq' });

  useEffect(() => {
    segment.track('onboarding_idnow_call_aborted');
  }, [segment]);

  const errorMessageIdByReason: Record<IdNowErrorKeys, string> = {
    CONNECTION_ISSUES: 'qonto-idnow.error-screen.error-reason.subtitle-connection-issues',
    IDNOW_ISSUE: 'qonto-idnow.error-screen.error-reason.subtitle-idnow-issue',
    NO_AUDIO: 'qonto-idnow.error-screen.error-reason.subtitle-no-audio',
    NO_VIDEO: 'qonto-idnow.error-screen.error-reason.subtitle-no-video',
    WRONG_LANGUAGE: 'qonto-idnow.error-screen.error-reason.subtitle-wrong-language',
    BAD_AUDIO: 'qonto-idnow.error-screen.error-reason.subtitle-bad-audio',
    ID_NOT_SUPPORTED: 'qonto-idnow.error-screen.error-reason.subtitle-id-not-supported',
    GENERIC: 'qonto-idnow.error-screen.error-reason.subtitle-generic',
    BAD_VIDEO: 'qonto-idnow.error-screen.error-reason.subtitle-bad-video',
    ID_DAMAGED: 'qonto-idnow.error-screen.error-reason.subtitle-id-damaged',
    AGENT_ABORT: 'qonto-idnow.error-screen.error-reason.subtitle-agent-abort',
    USER_ABORT: 'qonto-idnow.error-screen.error-reason.subtitle-user-abort',
    NO_ID: 'qonto-idnow.error-screen.error-reason.subtitle-no-id',
    WRONG_PERSON: 'qonto-idnow.error-screen.error-reason.subtitle-wrong-person',
    IDENT_CODE_NOT_RECEIVED:
      'qonto-idnow.error-screen.error-reason.subtitle-ident-code-not-received',
    IDNOW_DISCONNECTED: 'qonto-idnow.error-screen.error-reason.subtitle-idnow-disconnected',
    ID_EXPIRED: 'qonto-idnow.error-screen.error-reason.subtitle-id-expired',
    ID_HOLOGRAM: 'qonto-idnow.error-screen.error-reason.subtitle-id-hologram',
    LIGHTNING: 'qonto-idnow.error-screen.error-reason.subtitle-lightning',
    VIDEO_GENERIC_ISSUE: 'qonto-idnow.error-screen.error-reason.subtitle-video-generic-issue',
    BACKGROUND_NOISE: 'qonto-idnow.error-screen.error-reason.subtitle-background-noise',
    AUDIO_GENERIC_ISSUE: 'qonto-idnow.error-screen.error-reason.subtitle-audio-generic-issue',
    WRONG_ID_CODE: 'qonto-idnow.error-screen.error-reason.subtitle-wrong-id-code',
    USER_ABORT_WHILE_WAITING:
      'qonto-idnow.error-screen.error-reason.subtitle-user-abort-while-waiting',
    OTHER_DEVICE: 'qonto-idnow.error-screen.error-reason.subtitle-other-device',
    ID_GENERIC_ISSUE: 'qonto-idnow.error-screen.error-reason.subtitle-generic',
    STALLED_TIMEOUT: 'qonto-idnow.error-screen.error-reason.subtitle-generic',
    CALL_QUALITY_FAIL: 'qonto-idnow.error-screen.error-reason.subtitle-connection-pre-check',
    NO_VIDEO_IDENT_CONSENT: 'qonto-idnow.error-screen.error-reason.subtitle-no-video-ident-consent',
    WRONG_ID_NUMBER: 'qonto-idnow.error-screen.error-reason.subtitle-wrong-id-number',
    QES_ISSUE: 'qonto-idnow.error-screen.error-reason.subtitle-qes-issue',
    WRONG_PHONE_NUMBER: 'qonto-idnow.error-screen.error-reason.subtitle-wrong-phone-number',
    NO_AGENTS: 'qonto-idnow.error-screen.error-reason.subtitle-no-agents',
  };

  // Runtime safety: host apps can sometimes pass an unknown `reason` string.
  const resolvedErrorMessageId =
    reason && Object.prototype.hasOwnProperty.call(errorMessageIdByReason, reason)
      ? errorMessageIdByReason[reason]
      : errorMessageIdByReason.ID_GENERIC_ISSUE;

  return (
    <div className={styles.wrapper} data-test-idnow-step-error>
      <div className={styles.container}>
        <div className="mb-32">
          <Error404 className={styles.icon} />
        </div>
        <h1 className="heading-section mb-16">
          <FormattedMessage id="qonto-idnow.error-screen.title" />
        </h1>
        <p className="body-standard mb-32" data-test-idnow-error-step-description>
          <FormattedMessage
            id={resolvedErrorMessageId}
            values={{
              faqLink: (chunks: ReactNode) => (
                <a className="body-standard-link" href={faqHref} rel="noopener" target="_blank">
                  {chunks}
                </a>
              ),
            }}
          />
        </p>
        <Button
          className="mb-16"
          data-test-idnow-error-step-try-again-btn
          onPress={onTryAgain}
          stretch
          type="button"
        >
          <FormattedMessage id="qonto-idnow.error-screen.primary-cta" />
        </Button>
        {!isMobile && (
          <Button
            className="mb-16"
            data-test-idnow-error-step-switch-to-mobile-btn
            onPress={onSwitchToPhone}
            stretch
            type="button"
            variant="secondary"
          >
            <FormattedMessage id="qonto-idnow.error-screen.secondary-cta" />
          </Button>
        )}
      </div>
    </div>
  );
}
